import { Department } from "@/interfaces/department";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import useCustomMutation from "./useCustomMutation";

interface Params {
  id?: number;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

const getDepartment = async (param: Params): Promise<Department> => {
  const { data } = await axios.get<Department>(`/api/departments/${param.id}`);
  return data;
};

const updateDepartment = async (param: Department) => {
  const { data } = await axios.put(`/api/departments/${param.id}`, param);
  return data;
};

const deleteDepartment = async (param: Params) => {
  const { data } = await axios.delete(`/api/departments/${param.id}`);
  return data;
};

const useDepartments = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();
  const { data: department, isLoading } = useQuery(
    ["department", params?.id],
    () =>
      getDepartment({
        id: params?.id!,
      }),
    {
      enabled: immediate,
    },
  );

  const updateMutation = useMutation(
    ({ params }: MutationParams<Department>) => updateDepartment(params),
    useCustomMutation(toast, "update"),
  );
  const deleteMutation = useMutation(
    ({ params }: MutationParams<Params>) => deleteDepartment(params),
    useCustomMutation(toast, "delete"),
  );

  return {
    department,
    isLoading,

    updateMutation,
    updateDepartment: updateMutation.mutate,
    updateDepartmentAsync: updateMutation.mutateAsync,
    updateDepartmentIsLoading: updateMutation.isLoading,

    deleteMutation,
    deleteDepartment: deleteMutation.mutate,
    deleteDepartmentAsync: deleteMutation.mutateAsync,
    deleteDepartmentIsLoading: deleteMutation.isLoading,
  };
};

export default useDepartments;
